import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useTranslations } from 'hooks/useTranslations';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Resolver, useFieldArray, useForm } from 'react-hook-form';
import {
  EgoodReasonValues,
  EsafeStorageValues,
  ILicenceSafeStorage,
  ILicenceTemplateResponse,
} from 'models/licenceConfigurator';
import { Accordion } from 'components/molecules/Accordion';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { FormSelect } from 'components/molecules/FormSelect';
import { Box, Stack, Typography } from '@mui/material';
import { Table } from 'components/molecules/Table';
import { StyledFormElementWrapper, StyledPageTitle } from 'theme/styles';
import {
  EDictionaryPrefixTypesUniqueId,
  findInDictionary,
  mapDictionaryToOptionProp,
} from 'helpers/dictionary';
import { useConfigurationLicence } from 'pages/LicenceConfigurationPage/hooks';
import { IOrgLegalEntityData } from 'components/organisms/OrgLegalEntityDetails';
import {
  ILegalEntityCustomField,
  ILegalEntityData,
} from 'components/organisms/LegalEntityDetails';
import { EDictionaryTypes, IDictionary } from 'models/dictionary';
import { FormNumberInput } from 'components/molecules/FormNumberInput';
import { FormCheckbox } from 'components/molecules/FormCheckbox';
import FormLabel from 'components/atoms/FormLabel';
import { EButtonVariants } from 'constants/Buttons';
import Button from 'components/atoms/Button';
import { ERouteLinks } from 'models/route';
import {
  IDocument,
  ILegalEntityExtendedAddress,
  ILegalEntityLicenceFormValues,
} from 'models/legalEntity';
import { IOwner, IOwnerIndividual, IOwnerOrganisation } from 'models/licence';
import { EFormInputTypes, FormInput } from 'components/molecules/FormInput';
import { yupResolver } from '@hookform/resolvers/yup';
import { useScroll } from 'hooks/useScroll';
import { ActionTypes } from 'state/actions/alert';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { mapBlankToNull } from 'utils/form';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { FormListWrapper } from 'components/atoms/FormListWrapper';
import { formatAddress } from 'utils/legalAddress';
import { useLang } from 'models/langContext';
import { useDictionary } from 'models/dictionaryContext';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import { formatOnlyDateToRequest } from 'utils/date';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import { FormWyswigEditor } from 'components/molecules/FormWyswigEditor';
import { LETypes } from 'constants/LegalEntityTypes';
import { calculatePeriod } from 'helpers/calculatePeriod';
import {
  documentsTableColumns,
  mapDocumentsToDataSource,
  prepareCustomFieldPayload,
} from '../helpers';
import { DocumentForm } from './documentForm';
import { useLegalEntities } from '../hooks';
import { useFormSchema } from './schema';
import { mapLEExtendedAddressToSafeStorage, safeStorageTableColumns } from './helpers';
import { LicenceFormOwnerDetails } from './LicenceFormOwnerDetails';
import { StyledLicenceWrapper } from './styles';
import { getGalleryMain, getGalleryMainPreview } from 'requests/gallery';
import { ESize } from 'models/galery';
import { ICase } from 'models/cases';
import { ETabDetailsId } from 'pages/CaseManagementPage/CasesPage/CaseDetailsPage';
import { EIconTypes } from 'constants/Icons';
import { ECustomFieldObjectType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ICustomFieldForm } from '../IndividualSearchForm';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { EFieldsOptional } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/helpers';
import { CardList } from 'components/molecules/CardList';
import { AttachmentsCard } from './AttachmentsCard';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';

interface ILegalEntityLicenceForm {
  licence: ILicenceTemplateResponse;
  caseData: ICase;
  initialFormValues: ILegalEntityLicenceFormValues;
  legalEntity: ILegalEntityData | IOrgLegalEntityData | null;
  accessibleWeaponLicenceGoodReason: IDictionary[];
  primaryAddress: ILegalEntityExtendedAddress | undefined;
  availableAddresses: ILegalEntityExtendedAddress[];
  isFromLE: boolean;
}

enum ELicenceFieldsOptional {
  height = 'height',
  departmentName = 'departmentName',
  occupation = 'occupation',
  secondSurname = 'secondSurname',
  placeOfBirth = 'placeOfBirth',
  dateOfBirth = 'dateOfBirth',
  primaryWeaponLicencingAuthorityUniqueId = 'primaryWeaponLicencingAuthorityUniqueId',
  nationality = 'nationality',
  identityDocumentNumber = 'identityDocumentNumber',
}

const LegalEntityLicenceForm = ({
  licence,
  caseData,
  initialFormValues,
  legalEntity,
  accessibleWeaponLicenceGoodReason,
  primaryAddress,
  availableAddresses,
  isFromLE,
}: ILegalEntityLicenceForm) => {
  const { t } = useTranslations();
  const [searchParams] = useSearchParams();
  const type = caseData.legalType as LETypes;
  const expand = searchParams.get('expand');
  const { selectedLanguage } = useLang();
  const navigate = useNavigate();
  const { isDesktop } = useThemeBreakpoint();
  const {
    handleCreateWeaponLicence,
    civilianPrefix,
    loadDictionaries,
    getCustomField,
    customFieldsLicence,
  } = useLegalEntities();
  const { schema } = useFormSchema(
    licence,
    type === LETypes.individual,
    customFieldsLicence,
    initialFormValues?.customFields as ILegalEntityCustomField[],
  );
  const { elRef, scrollToError } = useScroll();
  const { id = '' } = useParams<{ id: string }>();
  const { setAlert, clearAlert, samePageAlert } = useAlert();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [safeStorages, setSafeStorages] = useState<ILicenceSafeStorage[]>([]);
  const [image, setImage] = useState<any>(null);
  const [prefixForLE, setPrfixForLE] = useState<string>();
  const [saveButtonIsDisabled, setSaveButtonIsDisabled] = useState(false);
  const [addFileModal, setAddFileModal] = useState(false);
  const [missingDataFields, setMissingDataFields] = useState<Array<string>>([]);
  const { getFieldsOptional, requiredFieldsName } = useSystemFieldsConfiguration();

  const {
    weaponLicencingAuthority,
    region,
    country,
    weaponLicenceCompetency,
    weaponLicenceCategory,
    weaponLicenceCondition,
    attachmentDocumentTypeData,
  } = useConfigurationLicence();

  const { addressType } = useDictionary();

  useEffect(() => {
    if (legalEntity && type === LETypes.individual && !image) {
      (async function getLegalEntityPhotoInit() {
        const photo = await getGalleryMainPreview(legalEntity.uniqueId, ESize.THUMBNAIL);
        const photoData = await getGalleryMain(legalEntity.uniqueId);
        if (photo && photoData) {
          setImage(photo);
          setValue('galleryEntryUniqueId', photoData.uniqueId);
        } else {
          setValue('galleryEntryUniqueId', null);
        }
      })();
    }
  }, [legalEntity, type]);

  useEffect(() => {
    if (type === LETypes.individual && legalEntity && licence) {
      let missingFields: Array<string> = [];
      if (
        !legalEntity.secondSurname &&
        (requiredFieldsName.includes(EFieldsOptional.secondSurname) ||
          licence.owner.individual.secondSurname)
      ) {
        missingFields.push(ELicenceFieldsOptional.secondSurname);
      }
      if (
        !legalEntity.placeOfBirth &&
        (requiredFieldsName.includes(EFieldsOptional.placeOfBirth) ||
          licence.owner.individual.placeOfBirth)
      ) {
        missingFields.push(ELicenceFieldsOptional.placeOfBirth);
      }
      if (
        !legalEntity.height &&
        (requiredFieldsName.includes(EFieldsOptional.height) ||
          licence.owner.individual.height)
      ) {
        missingFields.push(ELicenceFieldsOptional.height);
      }
      if (
        !legalEntity.occupation &&
        (requiredFieldsName.includes(EFieldsOptional.occupation) ||
          licence.owner.individual.occupation)
      ) {
        missingFields.push(ELicenceFieldsOptional.occupation);
      }
      if (!legalEntity.nationalityUniqueId && licence.owner.individual.nationality) {
        missingFields.push(ELicenceFieldsOptional.nationality);
      }
      if (!legalEntity.dateOfBirth && licence.owner.individual.dateOfBirth) {
        missingFields.push(ELicenceFieldsOptional.dateOfBirth);
      }
      if (
        !legalEntity.identityDocumentNumber &&
        licence.owner.individual.documentTypeAndNumber
      ) {
        missingFields.push(ELicenceFieldsOptional.identityDocumentNumber);
      }
      if (!legalEntity.primaryWeaponLicencingAuthorityUniqueId) {
        missingFields.push(ELicenceFieldsOptional.primaryWeaponLicencingAuthorityUniqueId);
      }
      setMissingDataFields(missingFields);
    }
    if (type === LETypes.organization && legalEntity && licence) {
      let missingFields: Array<string> = [];

      if (
        !legalEntity.departmentName &&
        (requiredFieldsName.includes(EFieldsOptional.departmentName) ||
          licence.owner.organisation.departmentName)
      ) {
        missingFields.push(ELicenceFieldsOptional.departmentName);
      }
      setMissingDataFields(missingFields);
    }
  }, [licence, requiredFieldsName, legalEntity]);

  useEffect(() => {
    const isMissingPrimaryAddress = !primaryAddress;
    const hasNoAvailableAddresses = !availableAddresses.length;

    if (hasNoAvailableAddresses || isMissingPrimaryAddress) {
      setSaveButtonIsDisabled(true);
      return;
    }

    if (missingDataFields.length) {
      setSaveButtonIsDisabled(true);
      return;
    }

    if (type === LETypes.individual) {
      const missingIdentityDocs =
        !legalEntity?.identityDocumentNumber || !legalEntity?.identityDocumentTypeUniqueId;
      const requiredIdentityDocs = licence.owner.individual.documentTypeAndNumber;

      if (missingIdentityDocs && requiredIdentityDocs) {
        setSaveButtonIsDisabled(true);
      } else {
        setSaveButtonIsDisabled(false);
      }
    } else if (type === LETypes.organization) {
      if (licence.owner.organisation.departmentName && !legalEntity?.departmentName) {
        setSaveButtonIsDisabled(true);
      } else {
        setSaveButtonIsDisabled(false);
      }
    }
  }, [
    type,
    legalEntity,
    licence.owner.individual.documentTypeAndNumber,
    primaryAddress,
    availableAddresses.length,
    legalEntity?.departmentName,
    licence.owner.organisation.departmentName,
  ]);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    loadDictionaries([EDictionaryTypes.civilianPrefix]);
  }, [selectedLanguage]);

  useEffect(() => {
    (async function init() {
      await loadDictionaries([EDictionaryTypes.civilianPrefix]);
      await getCustomField(ECustomFieldObjectType.LICENCE);
      getFieldsOptional();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<ILegalEntityLicenceFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(schema) as unknown as Resolver<ILegalEntityLicenceFormValues>,
  });

  const watchGeneralStartDate = watch('general.startDate');
  const watchHasSafeStorage = watch('conditions.hasSafeStorage');

  useEffect(() => {
    (async () => {
      if (errors) {
        const errorList: any = [];
        const keys = Object.keys(errors);
        keys.map((key) => {
          const items = (errors as Record<string, any[]>)[key];
          // eslint-disable-next-line no-return-assign
          Object.keys(items)?.forEach((k: any) => (errorList[`${key}.${k}`] = items[k]));
          return null;
        });
        scrollToError(errorList);
      }
    })();
  }, [errors]);

  useEffect(() => {
    setValue('conditions.safeStorages', safeStorages);
  }, [safeStorages]);

  useEffect(() => {
    if (civilianPrefix?.length && legalEntity) {
      const prefix = civilianPrefix?.find(
        (item: IDictionary) => item?.uniqueId === legalEntity?.prefixValueUniqueId,
      )?.name;
      setPrfixForLE(prefix);
    } else {
      setPrfixForLE('');
    }
  }, [civilianPrefix, legalEntity]);

  // eslint-disable-next-line consistent-return
  const onSubmit = async (data: any) => {
    setIsSubmitLoading(true);

    delete data.suitabilityAssessmentPassed;
    if (type !== LETypes.individual) {
      delete data.galleryEntryUniqueId;
    }

    let individual: IOwnerIndividual | undefined;
    let organisation: IOwnerOrganisation | undefined;
    if (type === LETypes.individual) {
      const ownerInd = licence.owner.individual;
      individual = {
        firstName: legalEntity?.firstName,
        lastName: legalEntity?.lastName,
        middleNames: legalEntity?.middleNames,
        genderUniqueId: ownerInd.gender ? legalEntity?.genderUniqueId : undefined,
        dateOfBirth: ownerInd.dateOfBirth
          ? formatOnlyDateToRequest(legalEntity?.dateOfBirth)
          : null,
        placeOfBirth: ownerInd.placeOfBirth ? legalEntity?.placeOfBirth : undefined,
        nationalityUniqueId: ownerInd.nationality
          ? legalEntity?.nationalityUniqueId
          : undefined,
        height: ownerInd.height ? legalEntity?.height : undefined,
        occupation: ownerInd.occupation ? legalEntity?.occupation : undefined,
        secondSurname: ownerInd.secondSurname ? legalEntity?.secondSurname : undefined,
        identityDocumentTypeUniqueId: ownerInd.documentTypeAndNumber
          ? mapBlankToNull(legalEntity?.identityDocumentTypeUniqueId)
          : undefined,
        identityDocumentNumber: ownerInd.documentTypeAndNumber
          ? mapBlankToNull(legalEntity?.identityDocumentNumber)
          : undefined,
        prefixValueUniqueId: legalEntity?.prefixValueUniqueId,
      };
    } else {
      const ownerOrg = licence.owner.organisation;
      organisation = {
        organisationName: ownerOrg.organisationName
          ? legalEntity?.organizationName
          : undefined,
        departmentName: ownerOrg.departmentName ? legalEntity?.departmentName : undefined,
        organisationCategoryUniqueId: legalEntity?.organisationCategoryUniqueId,
        organisationTypeUniqueIds: ownerOrg.organisationType
          ? legalEntity?.organisationTypeUniqueIds
          : undefined,
      };
    }

    const owner: IOwner | null = legalEntity
      ? {
          legalUniqueId: legalEntity.uniqueId,
          individual,
          organisation,
          primaryAddress: primaryAddress
            ? {
                typeUniqueId: primaryAddress?.typeUniqueId,
                line1: primaryAddress?.line1,
                line2: primaryAddress?.line2,
                city: primaryAddress?.city,
                postalCode: primaryAddress?.postalCode,
                regionUniqueId: primaryAddress?.regionUniqueId,
                countryUniqueId: primaryAddress?.countryUniqueId,
                latitude: primaryAddress?.latitude,
                longitude: primaryAddress?.longitude,
                storage: primaryAddress?.storage,
                addressUniqueId: primaryAddress?.uniqueId,
              }
            : null,
          displayUniqueId: legalEntity.displayUniqueId,
        }
      : null;

    if (!owner) return null;

    data.general.expiryDate = mapBlankToNull(data.general.expiryDate);

    data.general.name = licence?.general.name;
    data.general.code = licence?.general.code;

    data.conditions.areaOfValidityUniqueId = mapBlankToNull(
      data.conditions.areaOfValidityUniqueId,
    );

    if (licence.conditions.specificCountryArea) {
      delete data.conditions.areaOfValidityUniqueId;
    }

    data.conditions.competencyUniqueId = mapBlankToNull(data.conditions.competencyUniqueId);
    data.conditions.weaponCategorisationUniqueId = mapBlankToNull(
      data.conditions.weaponCategorisationUniqueId,
    );
    data.conditions.carry = mapBlankToNull(data.conditions.carry);
    data.conditions.additionalConditions = mapBlankToNull(
      data.conditions.additionalConditions,
    );
    data.conditions.safeStorageDescription = mapBlankToNull(
      data.conditions.safeStorageDescription,
    );

    const hasSafeStorage = watchHasSafeStorage?.toString() === 'true';
    data.conditions.safeStorages = hasSafeStorage ? safeStorages : [];
    data.conditions.hasSafeStorage =
      licence.conditions.safeStorage === null ? null : hasSafeStorage;

    data.caseUniqueId = caseData.uniqueId;

    data.customFields = [];

    const customFieldPayload: ICustomFieldForm[] = prepareCustomFieldPayload(
      data,
      customFieldsLicence,
    );

    if (customFieldPayload?.length && customFieldPayload.length > 0) {
      data.customFields = customFieldPayload;
    } else {
      delete data.customFields;
    }

    try {
      await handleCreateWeaponLicence(
        data,
        owner,
        isFromLE
          ? `${ERouteLinks.LegalEntityViewLicence.replace(':id', id as string)}?type=${
              type === LETypes.individual
                ? LEAccordionNames.INDIVIDUAL
                : LEAccordionNames.ORGANISATION
            }`
          : `${ERouteLinks.CaseDetailLicencePage.replace(':caseId', caseData.uniqueId as string)}?expand=${ETabDetailsId.licences} `,
      );
    } finally {
      setIsSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (watchGeneralStartDate && !licence.general.licenceDurationUnlimited) {
      const y = licence.general.licenceDurationYears;
      const m = licence.general.licenceDurationMonths;
      const d = licence.general.licenceDurationDays;
      const expiryDate = calculatePeriod(new Date(watchGeneralStartDate), {
        days: d,
        months: m,
        years: y,
      });
      setValue('general.expiryDate', expiryDate.toString());
    } else {
      setValue('general.expiryDate', '');
    }
  }, [watchGeneralStartDate]);

  useEffect(() => {
    const hasSafeStorage = watchHasSafeStorage?.toString() === 'true';
    if (hasSafeStorage) {
      setValue('conditions.safeStorageDescription', '');
    } else if (watchHasSafeStorage !== undefined) {
      setSafeStorages([]);
    }
  }, [watchHasSafeStorage, setValue]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'documents',
  });

  const documentSetToForm = async (document: IDocument) => {
    if (fields.length < 10) {
      append(document);
    } else {
      await setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
        text: t('addAttachment.maxFiles.text'),
        variant: EAlertVariants.error,
      });
    }
  };

  const handleDownload = (file: File) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteDocument = (documentId: number) => {
    remove(documentId);
  };

  return (
    <>
      <StyledPageTitle variant="h4">
        {t('caseManagement.cases.createLicence.label')}
      </StyledPageTitle>
      {!!missingDataFields.length && (
        <Grid sx={{ mb: 4 }}>
          <Alert text={''} variant={EAlertVariants.warning} sx={{ alignItems: 'flex-start' }}>
            <p style={{ marginTop: 0 }}>{t('legalEntity.missingDataLE1.text')}</p>
            <p style={{ marginLeft: 24 }}>
              {missingDataFields.map((li) => (
                <li>{t(`legalEntityDetails.${li}.label`)}</li>
              ))}
            </p>
          </Alert>
        </Grid>
      )}
      <form onSubmit={handleSubmit(onSubmit)}>
        <div ref={elRef}>
          <Accordion
            title={t('legalEntity.licence.generalInformation.label')}
            name="generalInformation"
            key="generalInformation"
            expanded
            alwaysExpanded
            hideExpandIcon
          >
            <StyledLicenceWrapper container size={{ xs: 12 }}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Stack>
                  <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                    {t('caseManagement.cases.licenceCodeName.label')}
                  </Typography>
                  <Typography variant="body2">
                    {licence?.general.code && licence?.general.name
                      ? `${licence?.general.code} ${licence?.general.name} `
                      : '-'}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Stack>
                  <Typography variant="subtitle2">
                    {t('legalEntity.licence.duration.label')}
                  </Typography>
                  {licence.general.licenceDurationUnlimited ? (
                    <Typography variant="body2">
                      {t('legalEntity.licence.general.licenceDurationUnlimited')}
                    </Typography>
                  ) : (
                    <Typography variant="body2">
                      {licence.general.licenceDurationYears
                        ? `${licence.general.licenceDurationYears} ${t('general.years.label')} `
                        : ''}
                      {licence.general.licenceDurationMonths
                        ? ` ${licence.general.licenceDurationMonths} ${t('general.month.label')} `
                        : ''}
                      {licence.general.licenceDurationDays
                        ? ` ${licence.general.licenceDurationDays} ${t('general.day.label')} `
                        : ''}
                    </Typography>
                  )}
                </Stack>
              </Grid>
              {licence?.owner.individual.ageLimit ? (
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Stack>
                    <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                      {t('legalEntity.licence.minimumAge.label')}
                    </Typography>
                    <Typography variant="body2">{`${licence?.owner.individual.ageLimit} ${t(
                      'general.yearYears.label',
                    )} `}</Typography>
                  </Stack>
                </Grid>
              ) : (
                ''
              )}
              {(legalEntity?.prefixGroupUniqueId ===
                EDictionaryPrefixTypesUniqueId.civilianPrefix ||
                legalEntity?.organisationCategoryUniqueId ===
                  EOrganisationCategory.civilian) && (
                <>
                  <Grid size={{ xs: 6, lg: 3 }}>
                    <Stack>
                      <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                        {t('legalEntity.licence.primaryLicensingAuthority.label')}
                      </Typography>
                      <Typography variant="body2">
                        {findInDictionary(
                          caseData.licenceAuthorityUniqueId,
                          weaponLicencingAuthority,
                        )}
                      </Typography>
                    </Stack>
                  </Grid>
                </>
              )}
            </StyledLicenceWrapper>
          </Accordion>
          <Grid container size={{ xs: 12 }} columnSpacing={2.5}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormDatePicker
                name="general.startDate"
                label={`${t('legalEntity.licence.startDate.label')}* `}
                minDate={new Date()}
                control={control}
                errors={errors}
              />
            </Grid>
            {!licence.general.licenceDurationUnlimited && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormDatePicker
                  name="general.expiryDate"
                  label={`${t('legalEntity.licence.expiryDate.label')}* `}
                  control={control}
                  errors={errors}
                  disabled
                />
              </Grid>
            )}
            <Grid sx={{ mt: 3 }}>
              <FormCheckbox
                name="suitabilityAssessmentPassed"
                label={`${t('legalEntity.licence.suitabilityAssessmentPassed.label')}* `}
                control={control}
                errors={errors}
              />
            </Grid>
          </Grid>
          <Accordion
            title={t('legalEntity.licence.licenceOwner.label')}
            name="licenceOwner"
            key="licenceOwner"
            expanded
            alwaysExpanded
            hideExpandIcon
          >
            {!primaryAddress && !formatAddress(primaryAddress, country) && (
              <Grid sx={{ mb: 2 }}>
                <Alert
                  text={t('legalEntity.licence.primaryAddressNotDefined.message')}
                  variant={EAlertVariants.warning}
                />
              </Grid>
            )}
            {licence.owner.individual.documentTypeAndNumber &&
              type === LETypes.individual &&
              (!legalEntity?.identityDocumentNumber ||
                !legalEntity?.identityDocumentTypeUniqueId) && (
                <Grid sx={{ mb: 2 }}>
                  <Alert
                    text={t('legalEntity.licence.identityDocumentNotDefined.message')}
                    variant={EAlertVariants.warning}
                  />
                </Grid>
              )}
            {licence.owner.organisation.departmentName &&
              type === LETypes.organization &&
              !legalEntity?.departmentName && (
                <Grid sx={{ mb: 2 }}>
                  <Alert
                    text={t('legalEntity.licence.departmentNameNotDefined.message')}
                    variant={EAlertVariants.warning}
                  />
                </Grid>
              )}
            <LicenceFormOwnerDetails
              legalEntity={legalEntity}
              primaryAddress={primaryAddress}
              prefixForLE={prefixForLE}
              image={image}
              owner={licence.owner}
              licenceMismatchingLegalEntity={null}
              type={type}
            />
          </Accordion>
          <Accordion
            title={t('legalEntity.licence.licenceConditions.label')}
            name="licenceConditions"
            key="licenceConditions"
            alwaysExpanded={true}
            hideExpandIcon
          />
          <Grid container size={{ xs: 12 }} columnSpacing={2.5}>
            {licence.conditions.competency && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={mapDictionaryToOptionProp(weaponLicenceCompetency)}
                  setValue={setValue}
                  name="conditions.competencyUniqueId"
                  label={`${t('legalEntity.licence.competency.label')}* `}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}
            {licence.conditions.carryConditions && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={mapDictionaryToOptionProp(weaponLicenceCondition)}
                  setValue={setValue}
                  name="conditions.carry"
                  label={`${t('legalEntity.licence.carryConditions.label')}* `}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}
            {licence.conditions.specificCountryArea ? (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={[{ label: t('licence.areaRestriction.national'), value: '1' }]}
                  setValue={setValue}
                  canBeEmpty={false}
                  name="conditions.areaOfValidityUniqueId"
                  label={t('legalEntity.licence.areaOfValidity.label')}
                  control={control}
                  errors={errors}
                  required
                />
              </Grid>
            ) : (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={mapDictionaryToOptionProp(region)}
                  setValue={setValue}
                  name="conditions.areaOfValidityUniqueId"
                  label={`${t('legalEntity.licence.areaOfValidity.label')}* `}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}
            {licence.conditions.weaponCategory && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormSelect
                  options={mapDictionaryToOptionProp(weaponLicenceCategory)}
                  setValue={setValue}
                  name="conditions.weaponCategorisationUniqueId"
                  label={`${t('legalEntity.licence.weaponCategorisation.label')}* `}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}
            {licence.conditions.weaponNumberLimit && (
              <Grid size={{ xs: 12, md: 4 }}>
                <FormNumberInput
                  name="conditions.weaponNumberLimit"
                  label={`${t('legalEntity.licence.weaponNumberLimit.label')}* `}
                  control={control}
                  errors={errors}
                />
              </Grid>
            )}
            {licence.conditions.additionalConditions && (
              <Grid size={{ xs: 12 }}>
                <FormWyswigEditor
                  name="conditions.additionalConditions"
                  label={t('legalEntity.licence.additionalConditions.label')}
                  setValue={setValue}
                  control={control}
                  errors={errors}
                  setError={setError}
                  clearErrors={clearErrors}
                  charsLimit={1000}
                  withValidation
                  required
                />
              </Grid>
            )}
            <Grid container size={{ xs: 12 }}>
              {licence.conditions.goodReasonDictionaryIds.length > 0 && (
                <Grid sx={{ mb: 0.5 }}>
                  <FormLabel
                    name="conditions.goodReasonUniqueIds"
                    label={t('legalEntity.licence.goodReason.label')}
                    required
                  />
                </Grid>
              )}
              <FormListWrapper
                isError={!!errors.conditions?.goodReasonUniqueIds}
                errorMessage={errors.conditions?.goodReasonUniqueIds?.root?.message?.toString()}
              >
                <Grid container size={{ xs: 12 }}>
                  {accessibleWeaponLicenceGoodReason &&
                    accessibleWeaponLicenceGoodReason.map((item: IDictionary) => (
                      <Grid size={{ xs: 12, md: 4, sm: 6 }}>
                        <FormCheckbox
                          name={`conditions.goodReasonUniqueIds.${item.uniqueId}`}
                          label={item.name}
                          control={control}
                          errors={errors}
                          withValidation={false}
                          booleanType
                        />
                      </Grid>
                    ))}
                </Grid>
              </FormListWrapper>
            </Grid>
          </Grid>

          <Grid size={{ xs: 12 }}>
            {licence.conditions.goodReason === EgoodReasonValues.Text && (
              <FormInput
                name="conditions.goodReason"
                type={EFormInputTypes.textarea}
                minRows={3}
                multiline
                charsLimit={1000}
                showCounter
                label={t('legalEntity.licence.goodReason.single.label')}
                control={control}
                errors={errors}
                setValue={setValue}
                withValidation
                required
              />
            )}
          </Grid>
          {licence.conditions.safeStorage && (
            <>
              <Accordion
                title={t('legalEntity.licence.licenceConditions.safeStorage')}
                name="licenceConditions"
                key="licenceConditions"
                alwaysExpanded={true}
                hideExpandIcon
              />
              {licence.conditions.safeStorage === EsafeStorageValues.Text && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <FormRadioGroup
                      name="conditions.hasSafeStorage"
                      label={t('legalEntity.licence.licenceConditions.hasSafeStorage.label')}
                      options={[
                        {
                          label: t(
                            'legalEntity.licence.licenceConditions.hasSafeStorage.yes.label',
                          ),
                          value: 'true',
                        },
                        {
                          label: t(
                            'legalEntity.licence.licenceConditions.hasSafeStorage.no.label',
                          ),
                          value: 'false',
                        },
                      ]}
                      control={control}
                      errors={errors}
                      booleanType
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <FormWyswigEditor
                      name="conditions.safeStorageDescription"
                      label={t('legalEntity.licence.safeStorageDescription.label')}
                      setValue={setValue}
                      control={control}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      charsLimit={1000}
                      withValidation
                      required
                      showCounter
                    />
                  </Grid>
                </>
              )}
              {licence.conditions.safeStorage === EsafeStorageValues.Object && (
                <Grid size={{ xs: 12 }}>
                  <Grid sx={{ mb: 2 }}>
                    <FormRadioGroup
                      name="conditions.hasSafeStorage"
                      label={t('legalEntity.licence.licenceConditions.hasSafeStorage.label')}
                      options={[
                        {
                          label: t(
                            'legalEntity.licence.licenceConditions.hasSafeStorage.yes.label',
                          ),
                          value: 'true',
                        },
                        {
                          label: t(
                            'legalEntity.licence.licenceConditions.hasSafeStorage.no.label',
                          ),
                          value: 'false',
                        },
                      ]}
                      control={control}
                      errors={errors}
                      booleanType
                      required
                    />
                  </Grid>
                  {watchHasSafeStorage && watchHasSafeStorage !== '' && (
                    <div>
                      {watchHasSafeStorage === 'true' ? (
                        <StyledFormElementWrapper>
                          {availableAddresses.filter((address) => address?.storage).length ? (
                            <>
                              <Table
                                columns={safeStorageTableColumns({
                                  selectedStorage: safeStorages,
                                  setSelectedStorage: setSafeStorages,
                                  t,
                                })}
                                dataSource={mapLEExtendedAddressToSafeStorage(
                                  availableAddresses,
                                  region,
                                  country,
                                  addressType,
                                  id as string,
                                )}
                              />
                              {errors.conditions?.safeStorages && (
                                <FormErrorLabel
                                  label={
                                    (errors.conditions?.safeStorages as { message?: string })
                                      ?.message ?? 'asdasd'
                                  }
                                />
                              )}
                            </>
                          ) : (
                            <Alert
                              text={t(
                                'legalEntity.licence.storageAddressIsNotDefined.message',
                              )}
                              variant={EAlertVariants.error}
                            />
                          )}
                        </StyledFormElementWrapper>
                      ) : (
                        <Grid size={{ xs: 12 }}>
                          <FormWyswigEditor
                            name="conditions.safeStorageDescription"
                            label={t('legalEntity.licence.safeStorageDescription.label')}
                            setValue={setValue}
                            control={control}
                            errors={errors}
                            setError={setError}
                            clearErrors={clearErrors}
                            charsLimit={1000}
                            withValidation
                            showCounter
                            required
                          />
                        </Grid>
                      )}
                    </div>
                  )}
                </Grid>
              )}
            </>
          )}
          <Accordion
            title={t('legalEntity.licence.documents.label')}
            name="documents"
            key="documents"
            alwaysExpanded
            hideExpandIcon
          >
            <Grid sx={{ mb: 2, textAlign: 'right' }}>
              <Button
                id="legalEntity_licence_addDocument-button"
                variant={EButtonVariants.contained}
                icon={EIconTypes.plus}
                label={t('legalEntity.licence.addDocument.label')}
                onClick={() => setAddFileModal(true)}
              />
            </Grid>
            <DocumentForm
              setToForm={documentSetToForm}
              addFileModal={addFileModal}
              setAddFileModal={setAddFileModal}
            />
            {isDesktop ? (
              <Table
                columns={documentsTableColumns({
                  handleDownload: (file) => {
                    handleDownload(file);
                  },
                  handleDelete: (documentId: number) => handleDeleteDocument(documentId),
                })}
                dataSource={mapDocumentsToDataSource(fields, attachmentDocumentTypeData)}
              />
            ) : (
              <CardList
                items={mapDocumentsToDataSource(fields, attachmentDocumentTypeData)}
                render={(data, index) => (
                  <AttachmentsCard
                    data={data}
                    key={index}
                    handleDownload={(file) => handleDownload(file)}
                    handleDelete={(documentId: number) => handleDeleteDocument(documentId)}
                  />
                )}
              />
            )}
          </Accordion>

          <Accordion
            title={t('legalEntity.licence.additionalInformation.label')}
            name="additionalInformation"
            key="additionalInformation"
            alwaysExpanded
            hideExpandIcon
          >
            <Grid size={{ xs: 12 }}>
              <FormInput
                name="additionalInformation"
                type={EFormInputTypes.textarea}
                minRows={3}
                multiline
                label={t('legalEntity.licence.additionalInformation.label')}
                control={control}
                errors={errors}
                setValue={setValue}
                charsLimit={1000}
                showCounter
              />
            </Grid>
          </Accordion>

          {!!customFieldsLicence.length && (
            <Accordion
              title={t('legalEntity.licence.customFields.label')}
              name="customFields"
              key="customFields"
              alwaysExpanded
              hideExpandIcon
            >
              <Grid container columnSpacing={2.5} sx={{ rowGap: 1 }}>
                {customFieldsLicence.map((customField) => (
                  <Grid size={{ xs: 12, lg: 4 }} key={customField.uniqueId}>
                    <FormCustomField
                      data={customField}
                      control={control}
                      errors={errors}
                      setValue={setValue}
                    />
                  </Grid>
                ))}
              </Grid>
            </Accordion>
          )}
          <Grid sx={{ mt: 2 }}>
            {samePageAlert && (
              <Box sx={{ mt: 2 }}>
                <Alert text={samePageAlert.text} variant={samePageAlert.variant} />
              </Box>
            )}
          </Grid>
          <Grid container columnSpacing={3} sx={{ mt: 4, rowGap: 3 }}>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Button
                fullWidth
                label={t('general.cancel.button')}
                id="cancel"
                variant={EButtonVariants.outlined}
                onClick={() =>
                  navigate(
                    isFromLE
                      ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':id', id as string).replace(':caseId', caseData.uniqueId as string)}?type=${
                          type === LETypes.individual
                            ? LEAccordionNames.INDIVIDUAL
                            : LEAccordionNames.ORGANISATION
                        }&expand=${expand}`
                      : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseData.uniqueId as string)}?expand=${ETabDetailsId.licences}?expand=${expand}`,
                  )
                }
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 3 }}>
              <Button
                type="submit"
                fullWidth
                label={t('general.save.button')}
                id="save"
                variant={EButtonVariants.contained}
                disabled={saveButtonIsDisabled}
                isLoading={isSubmitLoading}
              />
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ mt: 2 }}>
              <Typography variant="caption">{t('general.mandatoryField.text')}</Typography>
            </Grid>
          </Grid>
        </div>
      </form>
    </>
  );
};

export { LegalEntityLicenceForm };
